import React, { useEffect, useState } from 'react';
import Popup from 'reactjs-popup';
import api from '../../api';
import { ToastContainer, toast } from 'react-toastify';
import { Button, Input } from '../../components/Common';
import { MAX_CAPACITY_KWH, MIN_CAPACITY_KWH } from '../../constants/batteryCapacity';

const TOAST_OPTIONS = {
    position: 'top-center',
    autoClose: 300,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    progress: undefined,
};

// The API reports failures as { error }. Fall back to the Error's own message rather than
// interpolating the object itself, which renders as "[object Object]".
const errorMessage = err => err?.response?.data?.error || err?.message || 'Please try again.';

// Lets an operator set, correct, or clear a gateway's battery capacity. The value is the
// NAMEPLATE (gross) figure from the battery's label — the gateway derives usable energy
// itself by scaling it by its SoC window, so entering a usable figure would scale it twice.
// It takes precedence over whatever the inverter reports until cleared.
const EditBatteryCapacityModal = ({ deviceId, capacity, open, setOpen, onSaved }) => {
    const currentOverride = capacity?.override_kwh ?? null;
    const deviceReported = capacity?.device_reported_kwh ?? null;

    const [value, setValue] = useState(currentOverride === null ? '' : String(currentOverride));
    const [error, setError] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setValue(currentOverride === null ? '' : String(currentOverride));
    }, [currentOverride, open]);

    const handleClose = () => {
        setOpen(false);
        setError('');
        setValue(currentOverride === null ? '' : String(currentOverride));
    };

    const save = async () => {
        const trimmed = value.trim();
        if (trimmed === '') {
            setError('Enter a capacity, or use Clear to fall back to the inverter’s own figure.');
            return;
        }
        const parsed = Number(trimmed);
        if (!Number.isFinite(parsed) || parsed < MIN_CAPACITY_KWH || parsed > MAX_CAPACITY_KWH) {
            setError(`Capacity must be a number between ${MIN_CAPACITY_KWH} and ${MAX_CAPACITY_KWH} kWh.`);
            return;
        }
        setError('');
        setSaving(true);
        try {
            const resp = await api.set_device_gateway_battery_capacity(deviceId, parsed);
            onSaved(resp?.data ?? null);
            setOpen(false);
            toast.success('Battery capacity updated', TOAST_OPTIONS);
        } catch (err) {
            setError(`Could not update battery capacity. ${errorMessage(err)}`);
        } finally {
            setSaving(false);
        }
    };

    // Clearing is a distinct action rather than saving a blank value: it hands control back
    // to the inverter, which may report nothing at all, so it should be deliberate.
    const clear = async () => {
        setError('');
        setSaving(true);
        try {
            const resp = await api.clear_device_gateway_battery_capacity(deviceId);
            onSaved(resp?.data ?? null);
            setOpen(false);
            toast.success('Battery capacity cleared', TOAST_OPTIONS);
        } catch (err) {
            setError(`Could not clear battery capacity. ${errorMessage(err)}`);
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <ToastContainer />
            <Popup open={open} onClose={handleClose} modal className="modal" contentStyle={{ maxWidth: 440, width: '90%', borderRadius: 15 }}>
                <div>
                    <button type="button" className="modal__close" aria-label="Close battery capacity dialog" onClick={handleClose}>
                        &#10005;
                    </button>
                    <h5 className="modal__header"> Battery capacity </h5>
                    <div className="modal__content" style={{ alignItems: 'stretch', textAlign: 'left', padding: '24px 28px 8px' }}>
                        <label htmlFor="batteryCapacity" style={{ fontWeight: 600, marginBottom: 4 }}>
                            Nameplate capacity (kWh)
                        </label>
                        <Input
                            id="batteryCapacity"
                            type="number"
                            placeholder="13.5"
                            value={value}
                            onChange={e => setValue(e.target.value)}
                            fullWidth
                        />
                        <p style={{ margin: '6px 0 0', fontSize: 13, color: '#6b7280' }}>
                            The capacity printed on the battery label, not the usable capacity.
                        </p>
                        {deviceReported !== null && (
                            <p style={{ margin: '6px 0 0', fontSize: 13, color: '#6b7280' }}>The inverter reports {deviceReported} kWh.</p>
                        )}
                        {capacity?.diverges && (
                            <p style={{ margin: '6px 0 0', fontSize: 13, color: '#b45309' }}>
                                This differs materially from what the inverter reports. The value set here is the one in use.
                            </p>
                        )}
                        {error !== '' && (
                            <p className="modal__error-message" style={{ marginTop: 8 }}>
                                {error}
                            </p>
                        )}
                    </div>

                    <div className="modal__actions">
                        <Button text={saving ? 'Saving…' : 'Save'} onClick={save} disabled={saving} />
                        {currentOverride !== null && <Button text="Clear" onClick={clear} disabled={saving} className="modal__cancel" />}
                        <Button text="Cancel" onClick={handleClose} className="modal__cancel" />
                    </div>
                </div>
            </Popup>
        </>
    );
};

export default EditBatteryCapacityModal;
